import { addError } from '@infra/datadog/tracking';
import { DeviceIdService } from '@services/DeviceIdService';
import { TimeSyncService } from '@services/TimeSyncService';
import createClient from 'openapi-fetch';
import type { paths, components } from '@api/game/generated';

import { ApiError } from '@libs/errors';
import { SignalRSocket, SocketOptions } from '@libs/socket-signalr';

import { AuthService } from '@features/auth/services/AuthService';

const NAME = 'GameApi';

const GAME_API_CUSTOM_PING_MS = 3 * 1000;
const GAME_API_СUSTOM_TIMEOUT_MS = 6 * 1000;

/*
  ____,-------------------------------,____
  \   |          Http Client          |   /
  /___|-------------------------------|___\
*/

export { components, paths };

function getAccessKey() {
  return AuthService.instance.accessToken ?? '';
}

const api = createClient<paths>({
  baseUrl: __RUNTIME.gameApi,
});

api.use({
  async onRequest({ request }) {
    request.headers.set('X-Device-Id', DeviceIdService.instance.id);

    if (!request.headers.get('Authorization')) {
      request.headers.set('Authorization', `Bearer ${getAccessKey()}`);

      return request;
    }
  },

  async onResponse({ request, response }) {
    if (response.status === 401 && isCurrentToken(request)) {
      AuthService.instance.deleteTokens();
    }

    if (response.status >= 400) {
      const err = await ApiError.fromResponse(NAME, response);

      addError(err);
      throw err;
    }
    return undefined;
  },

  onError({ error, request }) {
    const err = ApiError.fromRequestError(NAME, request, error);

    addError(err);
    throw err;
  },
});

const { GET, POST } = api;
export { GET, POST };

/*
    ____,-------------------------------,____
    \   |         Socket Client         |   /
    /___|-------------------------------|___\
*/

// ------------------------------------------
// Game

const BASE_GAME_SOCKET_URL = `${__RUNTIME.gameWs}/hubs/game`;

export type GameSocketEvents =
  | components['schemas']['GameChangedEvent']
  | components['schemas']['DrawRequestEvent']
  | components['schemas']['DrawAcceptEvent']
  | components['schemas']['DrawDeclineEvent']
  | components['schemas']['SurrenderEvent'];

export class GameSocket extends SignalRSocket<GameSocketEvents> {
  constructor(
    public gameId: string,
    baseUrl: string = BASE_GAME_SOCKET_URL,
    customAccessToken?: string,
  ) {
    const EVENT_TYPES: Record<GameSocketEvents['eventType'], null> = {
      GameChanged: null,
      DrawRequest: null,
      DrawAccept: null,
      DrawDecline: null,
      Surrender: null,
    };

    const isGameApi = baseUrl === BASE_GAME_SOCKET_URL;

    const accessTokenFactory =
      (customAccessToken ? () => customAccessToken : undefined) ??
      (isGameApi ? AuthService.instance.socketAccessTokenFactory : undefined);

    const options: SocketOptions<GameSocketEvents> = {
      accessTokenFactory,

      onMsg: (e) => {
        TimeSyncService.instance.reportSocketTs(e.timestampMs);
      },
      pingMs: isGameApi ? GAME_API_CUSTOM_PING_MS : undefined,
      timeoutMs: isGameApi ? GAME_API_СUSTOM_TIMEOUT_MS : undefined,
    };

    const u = new URL(baseUrl);
    u.searchParams.append('gameId', gameId);

    if (AuthService.instance.user?.userId) {
      u.searchParams.append('userId', AuthService.instance.user?.userId);
    }

    super(u.toString(), Object.keys(EVENT_TYPES), options);
  }
}

// ------------------------------------------
// Round

const BASE_ROUND_SOCKET_URL = `${__RUNTIME.gameWs}/hubs/tournament`;

export type RoundSocketEvents =
  | components['schemas']['RoundGamesState']
  | components['schemas']['GameChangedEvent']
  | components['schemas']['DrawRequestEvent']
  | components['schemas']['DrawAcceptEvent']
  | components['schemas']['DrawDeclineEvent']
  | components['schemas']['SurrenderEvent'];

export class RoundSocket extends SignalRSocket<RoundSocketEvents> {
  constructor(
    public tournamentId: string,
    public roundNumber: number,
    baseUrl: string = BASE_ROUND_SOCKET_URL,
  ) {
    const EVENT_TYPES: Record<RoundSocketEvents['eventType'], null> = {
      RoundGamesState: null,
      GameChanged: null,
      DrawRequest: null,
      DrawAccept: null,
      DrawDecline: null,
      Surrender: null,
    };

    const isGameApi = baseUrl === BASE_ROUND_SOCKET_URL;

    const options: SocketOptions<RoundSocketEvents> = {
      accessTokenFactory: isGameApi ? AuthService.instance.socketAccessTokenFactory : undefined,

      onMsg: (e) => {
        TimeSyncService.instance.reportSocketTs(e.timestampMs);
      },
      pingMs: isGameApi ? GAME_API_CUSTOM_PING_MS : undefined,
      timeoutMs: isGameApi ? GAME_API_СUSTOM_TIMEOUT_MS : undefined,
    };

    const u = new URL(baseUrl);
    u.searchParams.append('tournamentId', tournamentId);
    u.searchParams.append('roundNumber', roundNumber.toString());

    if (AuthService.instance.user?.userId) {
      u.searchParams.append('userId', AuthService.instance.user?.userId);
    }

    super(u.toString(), Object.keys(EVENT_TYPES), options);
  }
}

function isCurrentToken(request: Request): boolean {
  const token = AuthService.instance.accessToken;
  return !!token && request.headers.get('Authorization') === `Bearer ${token}`;
}
